import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { AISummary } from "../components/AISummary";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  TrendingUp, 
  Wallet,
  CreditCard,
  Building2,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  PiggyBank,
  Home,
  FileText,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { toast } from "sonner";

const INVESTMENT_TYPES = [
  { value: "rrsp", label: "RRSP" },
  { value: "tfsa", label: "TFSA" },
  { value: "fhsa", label: "FHSA" },
  { value: "non_registered", label: "Non-Registered" },
  { value: "resp", label: "RESP" },
  { value: "lira", label: "LIRA" },
  { value: "pension", label: "Pension" },
  { value: "other", label: "Other" },
];

const ASSET_CATEGORIES = [
  { value: "property", label: "Property" },
  { value: "vehicle", label: "Vehicle" },
  { value: "collectibles", label: "Collectibles" },
  { value: "other", label: "Other" },
];

const LIABILITY_CATEGORIES = [
  { value: "student_loan", label: "Student Loan" },
  { value: "car_loan", label: "Car Loan" },
  { value: "mortgage", label: "Mortgage" },
  { value: "personal_loan", label: "Personal Loan" },
  { value: "line_of_credit", label: "Line of Credit" },
  { value: "other", label: "Other" },
];

export const NetWorthPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [netWorth, setNetWorth] = useState(null);
  const [history, setHistory] = useState([]);
  
  // Dialog states
  const [investmentDialogOpen, setInvestmentDialogOpen] = useState(false);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [liabilityDialogOpen, setLiabilityDialogOpen] = useState(false);
  
  // Edit states
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [editingLiability, setEditingLiability] = useState(null);
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    bank: true,
    investments: true,
    otherAssets: false,
    creditCards: true,
    otherLiabilities: false
  });
  
  // Form states
  const [investmentForm, setInvestmentForm] = useState({
    name: "",
    account_type: "tfsa",
    institution: "",
    balance: "",
    notes: ""
  });
  
  const [assetForm, setAssetForm] = useState({
    name: "",
    category: "property",
    value: "",
    notes: ""
  });
  
  const [liabilityForm, setLiabilityForm] = useState({
    name: "",
    category: "personal_loan",
    amount: "",
    interest_rate: "",
    notes: ""
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [netWorthRes, historyRes] = await Promise.all([
        api.get("/net-worth"),
        api.get("/net-worth/history")
      ]);
      setNetWorth(netWorthRes.data);
      setHistory(historyRes.data.history || []);
    } catch (error) {
      console.error("Failed to fetch net worth:", error);
      toast.error("Failed to load net worth");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split("-");
    return new Date(year, parseInt(month) - 1).toLocaleDateString("en-CA", {
      month: "short",
      year: "2-digit"
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Investment CRUD
  const handleSaveInvestment = async () => {
    try {
      if (editingInvestment) {
        await api.put(`/investments/${editingInvestment.id}`, {
          ...investmentForm,
          balance: parseFloat(investmentForm.balance)
        });
        toast.success("Investment updated");
      } else {
        await api.post("/investments", {
          ...investmentForm,
          balance: parseFloat(investmentForm.balance)
        });
        toast.success("Investment added");
      }
      setInvestmentDialogOpen(false);
      setEditingInvestment(null);
      setInvestmentForm({ name: "", account_type: "tfsa", institution: "", balance: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to save investment");
    }
  };

  const handleEditInvestment = (inv) => {
    setEditingInvestment(inv);
    setInvestmentForm({
      name: inv.name,
      account_type: inv.account_type,
      institution: inv.institution,
      balance: inv.balance.toString(),
      notes: inv.notes || ""
    });
    setInvestmentDialogOpen(true);
  };

  const handleDeleteInvestment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this investment?")) return;
    try {
      await api.delete(`/investments/${id}`);
      toast.success("Investment deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete investment");
    }
  };

  // Other Asset CRUD
  const handleSaveAsset = async () => {
    try {
      if (editingAsset) {
        await api.put(`/other-assets/${editingAsset.id}`, {
          ...assetForm,
          value: parseFloat(assetForm.value)
        });
        toast.success("Asset updated");
      } else {
        await api.post("/other-assets", {
          ...assetForm,
          value: parseFloat(assetForm.value)
        });
        toast.success("Asset added");
      }
      setAssetDialogOpen(false);
      setEditingAsset(null);
      setAssetForm({ name: "", category: "property", value: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to save asset");
    }
  };

  const handleEditAsset = (asset) => {
    setEditingAsset(asset);
    setAssetForm({
      name: asset.name,
      category: asset.category,
      value: asset.value.toString(),
      notes: asset.notes || ""
    });
    setAssetDialogOpen(true);
  };

  const handleDeleteAsset = async (id) => {
    if (!window.confirm("Are you sure you want to delete this asset?")) return;
    try {
      await api.delete(`/other-assets/${id}`);
      toast.success("Asset deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete asset");
    }
  };

  // Other Liability CRUD
  const handleSaveLiability = async () => {
    try {
      const data = {
        ...liabilityForm,
        amount: parseFloat(liabilityForm.amount),
        interest_rate: liabilityForm.interest_rate ? parseFloat(liabilityForm.interest_rate) : null
      };
      
      if (editingLiability) {
        await api.put(`/other-liabilities/${editingLiability.id}`, data);
        toast.success("Liability updated");
      } else {
        await api.post("/other-liabilities", data);
        toast.success("Liability added");
      }
      setLiabilityDialogOpen(false);
      setEditingLiability(null);
      setLiabilityForm({ name: "", category: "personal_loan", amount: "", interest_rate: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to save liability");
    }
  };

  const handleEditLiability = (liability) => {
    setEditingLiability(liability);
    setLiabilityForm({
      name: liability.name,
      category: liability.category,
      amount: liability.amount.toString(),
      interest_rate: liability.interest_rate?.toString() || "",
      notes: liability.notes || ""
    });
    setLiabilityDialogOpen(true);
  };

  const handleDeleteLiability = async (id) => {
    if (!window.confirm("Are you sure you want to delete this liability?")) return;
    try {
      await api.delete(`/other-liabilities/${id}`);
      toast.success("Liability deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete liability");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="networth-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 rounded-2xl" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-32 rounded-2xl" />
        </div>
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  const netWorthPositive = (netWorth?.net_worth || 0) >= 0;
  
  // Calculate change from previous month
  const lastMonth = history.length >= 2 ? history[history.length - 2]?.net_worth : 0;
  const currentMonth = history.length >= 1 ? history[history.length - 1]?.net_worth : netWorth?.net_worth;
  const monthlyChange = currentMonth - lastMonth;
  const percentChange = lastMonth > 0 ? ((monthlyChange / lastMonth) * 100) : 0;

  // Get breakdown items by category
  const bankAccounts = Object.entries(netWorth?.breakdown || {}).filter(([k]) => k.startsWith("🏦"));
  const creditCards = Object.entries(netWorth?.breakdown || {}).filter(([k]) => k.startsWith("💳"));
  const investmentItems = netWorth?.investment_accounts || [];
  const otherAssetItems = netWorth?.other_asset_items || [];
  const otherLiabilityItems = netWorth?.other_liability_items || [];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="networth-page">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight">Net Worth</h1>
          <p className="text-muted-foreground mt-1">
            Track your complete financial picture
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={investmentDialogOpen} onOpenChange={(open) => {
            setInvestmentDialogOpen(open);
            if (!open) {
              setEditingInvestment(null);
              setInvestmentForm({ name: "", account_type: "tfsa", institution: "", balance: "", notes: "" });
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="add-investment-btn">
                <PiggyBank className="w-4 h-4 mr-2" />
                Add Investment
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingInvestment ? "Edit Investment" : "Add Investment Account"}</DialogTitle>
                <DialogDescription>
                  Track your RRSP, TFSA, FHSA, and other investment accounts.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="inv-name">Account Name</Label>
                  <Input
                    id="inv-name"
                    placeholder="e.g., Wealthsimple TFSA"
                    value={investmentForm.name}
                    onChange={(e) => setInvestmentForm(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="investment-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-type">Account Type</Label>
                  <Select
                    value={investmentForm.account_type}
                    onValueChange={(v) => setInvestmentForm(prev => ({ ...prev, account_type: v }))}
                  >
                    <SelectTrigger data-testid="investment-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INVESTMENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-institution">Institution</Label>
                  <Input
                    id="inv-institution"
                    placeholder="e.g., Wealthsimple, Questrade"
                    value={investmentForm.institution}
                    onChange={(e) => setInvestmentForm(prev => ({ ...prev, institution: e.target.value }))}
                    data-testid="investment-institution-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-balance">Current Balance ($)</Label>
                  <Input
                    id="inv-balance"
                    type="number"
                    step="0.01"
                    placeholder="50000"
                    value={investmentForm.balance}
                    onChange={(e) => setInvestmentForm(prev => ({ ...prev, balance: e.target.value }))}
                    data-testid="investment-balance-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-notes">Notes (optional)</Label>
                  <Input
                    id="inv-notes"
                    placeholder="Any additional notes"
                    value={investmentForm.notes}
                    onChange={(e) => setInvestmentForm(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInvestmentDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveInvestment} data-testid="save-investment-btn">
                  {editingInvestment ? "Update" : "Add"} Investment
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={assetDialogOpen} onOpenChange={(open) => {
            setAssetDialogOpen(open);
            if (!open) {
              setEditingAsset(null);
              setAssetForm({ name: "", category: "property", value: "", notes: "" });
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="add-asset-btn">
                <Home className="w-4 h-4 mr-2" />
                Add Asset
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingAsset ? "Edit Asset" : "Add Other Asset"}</DialogTitle>
                <DialogDescription>
                  Track property, vehicles, or other valuable assets.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="asset-name">Asset Name</Label>
                  <Input
                    id="asset-name"
                    placeholder="e.g., Primary Residence"
                    value={assetForm.name}
                    onChange={(e) => setAssetForm(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="asset-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="asset-category">Category</Label>
                  <Select
                    value={assetForm.category}
                    onValueChange={(v) => setAssetForm(prev => ({ ...prev, category: v }))}
                  >
                    <SelectTrigger data-testid="asset-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSET_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="asset-value">Current Value ($)</Label>
                  <Input
                    id="asset-value"
                    type="number"
                    step="0.01"
                    placeholder="500000"
                    value={assetForm.value}
                    onChange={(e) => setAssetForm(prev => ({ ...prev, value: e.target.value }))}
                    data-testid="asset-value-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="asset-notes">Notes (optional)</Label>
                  <Input
                    id="asset-notes"
                    placeholder="Any additional notes"
                    value={assetForm.notes}
                    onChange={(e) => setAssetForm(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAssetDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveAsset} data-testid="save-asset-btn">
                  {editingAsset ? "Update" : "Add"} Asset
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={liabilityDialogOpen} onOpenChange={(open) => {
            setLiabilityDialogOpen(open);
            if (!open) {
              setEditingLiability(null);
              setLiabilityForm({ name: "", category: "personal_loan", amount: "", interest_rate: "", notes: "" });
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="add-liability-btn">
                <FileText className="w-4 h-4 mr-2" />
                Add Liability
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingLiability ? "Edit Liability" : "Add Other Liability"}</DialogTitle>
                <DialogDescription>
                  Track loans, mortgages, or other debts not from credit cards.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="liability-name">Liability Name</Label>
                  <Input
                    id="liability-name"
                    placeholder="e.g., Student Loan"
                    value={liabilityForm.name}
                    onChange={(e) => setLiabilityForm(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="liability-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="liability-category">Category</Label>
                  <Select
                    value={liabilityForm.category}
                    onValueChange={(v) => setLiabilityForm(prev => ({ ...prev, category: v }))}
                  >
                    <SelectTrigger data-testid="liability-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LIABILITY_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="liability-amount">Amount Owing ($)</Label>
                  <Input
                    id="liability-amount"
                    type="number"
                    step="0.01"
                    placeholder="25000"
                    value={liabilityForm.amount}
                    onChange={(e) => setLiabilityForm(prev => ({ ...prev, amount: e.target.value }))}
                    data-testid="liability-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="liability-rate">Interest Rate % (optional)</Label>
                  <Input
                    id="liability-rate"
                    type="number"
                    step="0.01"
                    placeholder="5.5"
                    value={liabilityForm.interest_rate}
                    onChange={(e) => setLiabilityForm(prev => ({ ...prev, interest_rate: e.target.value }))}
                    data-testid="liability-rate-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="liability-notes">Notes (optional)</Label>
                  <Input
                    id="liability-notes"
                    placeholder="Any additional notes"
                    value={liabilityForm.notes}
                    onChange={(e) => setLiabilityForm(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setLiabilityDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveLiability} data-testid="save-liability-btn">
                  {editingLiability ? "Update" : "Add"} Liability
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>


      {/* AI Summary */}
      <AISummary page="networth" />

      {/* Hero */}
      <div className="card hero" style={{padding:"28px 32px 30px",marginBottom:22}}><span className="lab">Total net worth</span>
        <div className="serif" style={{fontSize:52,fontWeight:600,letterSpacing:"-2px",color: netWorthPositive ? "#3F7D52" : "#B0603F",margin:"10px 0 6px"}}>{formatCurrency(netWorth?.net_worth || 0)}</div>
        <div className="muted" style={{fontSize:14}}>As of {netWorth?.date || new Date().toLocaleDateString()}</div>
      </div>

      {/* Assets / Liabilities Grid */}
      <div className="grid g-2" style={{marginBottom:24}}>
        <div className="card" style={{padding:"26px 28px"}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
            <div className="ai2"><PiggyBank size={21} stroke="#3F7D52" /></div>
            <span className="lab">Total assets</span>
          </div>
          <div className="nwn" style={{color:"#3F7D52"}}>{formatCurrency(netWorth?.total_assets || 0)}</div>
          <div style={{marginTop:12}}>
            <div className="nwl"><span>Bank accounts</span><b>{formatCurrency(netWorth?.bank_accounts || 0)}</b></div>
            <div className="nwl"><span>Investments</span><b>{formatCurrency(netWorth?.investments || 0)}</b></div>
          </div>
        </div>
        <div className="card" style={{padding:"26px 28px"}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
            <div className="ai2" style={{background:"rgba(176,96,63,.08)"}}><CreditCard size={21} stroke="#B0603F" /></div>
            <span className="lab">Total liabilities</span>
          </div>
          <div className="nwn" style={{color:"#B0603F"}}>{formatCurrency(netWorth?.total_liabilities || 0)}</div>
          <div style={{marginTop:12}}>
            <div className="nwl"><span>Credit cards</span><b>{formatCurrency(netWorth?.credit_cards || 0)}</b></div>
            <div className="nwl"><span>Loans</span><b>{formatCurrency(netWorth?.other_liabilities || 0)}</b></div>
          </div>
        </div>
      </div>

      {/* Account Breakdown */}
      <div className="sec-h" style={{margin:"4px 2px 14px"}}><h3 style={{fontSize:20}}>Account breakdown</h3></div>
      <div className="card">
        {bankAccounts.map(function(entry, i) {
          return (
            <div key={"bank"+i} className="acct">
              <div className="ai2"><Building2 size={20} stroke="#3F7D52" /></div>
              <div className="nm"><b>{entry[0].replace("🏦 ", "")}</b><small>Bank account</small></div>
              <div className="bal" style={{color:"#3F7D52"}}>{formatCurrency(entry[1])}</div>
            </div>
          );
        })}
        {investmentItems.map(function(inv) {
          return (
            <div key={inv.id} className="acct" onClick={function(){setEditingInvestment(inv);setInvestmentForm({name:inv.name,account_type:inv.account_type,institution:inv.institution||"",balance:String(inv.balance),notes:inv.notes||""});setInvestmentDialogOpen(true);}} style={{cursor:"pointer"}}>
              <div className="ai2"><TrendingUp size={20} stroke="#3F7D52" /></div>
              <div className="nm"><b>{inv.name} <span className="muted" style={{fontWeight:500}}>({inv.account_type?.toUpperCase()})</span></b><small>{inv.institution || "Manual"}</small></div>
              <div className="bal" style={{color:"#3F7D52"}}>{formatCurrency(inv.balance)}</div>
            </div>
          );
        })}
        {otherAssetItems.map(function(asset) {
          return (
            <div key={asset.id} className="acct" onClick={function(){setEditingAsset(asset);setAssetForm({name:asset.name,category:asset.category,value:String(asset.value),notes:asset.notes||""});setAssetDialogOpen(true);}} style={{cursor:"pointer"}}>
              <div className="ai2"><PiggyBank size={20} stroke="#3F7D52" /></div>
              <div className="nm"><b>{asset.name}</b><small>{ASSET_CATEGORIES.find(function(c){return c.value===asset.category;})?.label || "Asset"}</small></div>
              <div className="bal" style={{color:"#3F7D52"}}>{formatCurrency(asset.value)}</div>
            </div>
          );
        })}
        {creditCards.map(function(entry, i) {
          return (
            <div key={"credit"+i} className="acct">
              <div className="ai2" style={{background:"rgba(176,96,63,.08)"}}><CreditCard size={20} stroke="#B0603F" /></div>
              <div className="nm"><b>{entry[0].replace("💳 ", "")}</b><small>Credit card</small></div>
              <div className="bal" style={{color:"#B0603F"}}>−{formatCurrency(Math.abs(entry[1]))}</div>
            </div>
          );
        })}
        {otherLiabilityItems.map(function(liability) {
          return (
            <div key={liability.id} className="acct" onClick={function(){setEditingLiability(liability);setLiabilityForm({name:liability.name,category:liability.category,amount:String(liability.amount),interest_rate:String(liability.interest_rate||""),notes:liability.notes||""});setLiabilityDialogOpen(true);}} style={{cursor:"pointer"}}>
              <div className="ai2" style={{background:"rgba(176,96,63,.08)"}}><CreditCard size={20} stroke="#B0603F" /></div>
              <div className="nm"><b>{liability.name}</b><small>{LIABILITY_CATEGORIES.find(function(c){return c.value===liability.category;})?.label || "Liability"}</small></div>
              <div className="bal" style={{color:"#B0603F"}}>−{formatCurrency(Math.abs(liability.amount))}</div>
            </div>
          );
        })}
        {bankAccounts.length === 0 && investmentItems.length === 0 && otherAssetItems.length === 0 && creditCards.length === 0 && otherLiabilityItems.length === 0 && (
          <div className="acct" style={{justifyContent:"center"}}>
            <div className="nm" style={{textAlign:"center"}}><small className="muted">No accounts yet — connect a bank or add investments, assets and liabilities to build your net worth.</small></div>
          </div>
        )}
      </div>
    </div>
  );
};
